import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { environment } from '../../../environments/environment';
import { catchError, timeout, finalize, tap } from 'rxjs/operators';
import { of } from 'rxjs';

interface Exam {
  id: string;
  examTitle: string;
  valuerRank: string;
  registrationDeadline: string;
  examDate: string;
  examTime: string;
  examLocation: string;
  examFee: string;
  remarks: string;
  posterFile: string | null;
  certificateFile: string | null;
  createdAt: string;
  updatedAt: string;
  examStatusId: number;
}

interface UserExamRegistration {
  id: string;
  hoursStudied: string;
  studyDate: string;
  year: number;
  sessionTitle: string;
  notes: string;
  isVerified: boolean;
  enrolledAt: string;
  completedAt: string | null;
  progress: string;
  isCompleted: boolean;
  examResult?: boolean | null;   // null = ยังไม่ประกาศผล, true = ผ่าน, false = ไม่ผ่าน
  createdAt: string;
  updatedAt: string;
  userId: string;
  examId: string;
  examListStatusId: number;
  examListPaymentStatusId: number;
  paymentSlip: string | null;
  amount: string | null;
  originalSlipStatus: boolean;
  typeReceipt?: string | null;
  exam: Exam;
  user?: {
    id: string;
    email: string;
    cid: string;
    isActive: boolean;
  };
  examListStatus?: {
    id: number;
    name: string;
  };
  examListPaymentStatus?: {
    id: number;
    name: string;
  };
}

@Component({
  selector: 'app-user-check-exam-list',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './user-check-exam-list.component.html',
  styleUrl: './user-check-exam-list.component.scss'
})
export class UserCheckExamListComponent implements OnInit {
  examRegistrations: UserExamRegistration[] = [];
  isLoading = false;
  error: string | null = null;
  userId: string | null = null;

  // Summary statistics
  totalExams = 0;
  passedExams = 0;
  failedExams = 0;
  pendingResultExams = 0;
  currentYear = new Date().getFullYear();

  // Payment slip tracking
  selectedPaymentSlips: Map<string, { file: File; fileName: string; preview: string }> = new Map();
  uploadingSlips: Set<string> = new Set();
  imageModalUrl: string | null = null;

  // Receipt type tracking
  receiptTypes: Map<string, string> = new Map();

  // Transfer date-time tracking
  transferDateTimes: Map<string, string> = new Map();

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    console.log('UserCheckExamListComponent constructor called');
  }

  ngOnInit(): void {
    console.log('UserCheckExamListComponent initialized');
    this.loadUserExams();
  }

  loadUserExams(): void {
    console.log('Starting to load user exams...');
    this.error = null;

    // Get user ID from auth service
    const currentUser = this.authService.getCurrentUser();
    console.log('Current user from AuthService:', currentUser);

    if (!currentUser || !currentUser.id) {
      console.error('No user found or user has no ID');
      this.error = 'ไม่พบข้อมูลผู้ใช้ กรุณาเข้าสู่ระบบอีกครั้ง';
      setTimeout(() => this.router.navigate(['/login']), 2000);
      return;
    }

    this.userId = currentUser.id;
    const apiUrl = `${environment.apiUrl}/exam-lists/user/${this.userId}`;
    console.log('Fetching exam registrations from URL:', apiUrl);
    console.log('Environment API URL:', environment.apiUrl);

    // Fetch exam lists data from API
    this.http.get<UserExamRegistration[]>(apiUrl).pipe(
      tap(response => {
        console.log('🔍 Raw HTTP response received:', response);
        console.log('🔍 Response type:', typeof response);
        console.log('🔍 Is Array:', Array.isArray(response));
      }),
      timeout(30000),
      catchError((error: HttpErrorResponse) => {
        console.error('❌ HTTP Error occurred:', error);
        console.error('Error status:', error.status);
        console.error('Error statusText:', error.statusText);
        console.error('Error message:', error.message);
        console.error('Error error:', error.error);

        if (error.status === 0) {
          this.error = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบว่า Backend API กำลังทำงานที่ ' + environment.apiUrl;
        } else if (error.status === 404) {
          console.log('No exam registrations found for user, showing empty state');
          this.error = null;
        } else if (error.status === 401 || error.status === 403) {
          this.error = 'ไม่มีสิทธิ์เข้าถึงข้อมูล กรุณาเข้าสู่ระบบใหม่';
          setTimeout(() => this.router.navigate(['/login']), 2000);
        } else {
          this.error = `เกิดข้อผิดพลาดในการโหลดข้อมูล (HTTP ${error.status}: ${error.statusText})`;
        }

        this.cdr.detectChanges();
        return of([]);
      }),
      finalize(() => {
        console.log('🏁 API call completed');
        this.cdr.detectChanges();
      })
    ).subscribe({
      next: (data) => {
        console.log('✅ Exam lists data received in subscribe');
        console.log('📊 Number of records:', data?.length ?? 0);
        console.log('📦 Data:', data);

        if (!Array.isArray(data)) {
          console.warn('⚠️ Data is not an array, converting...');
          this.examRegistrations = [];
        } else {
          // Filter out registrations with missing exam data
          this.examRegistrations = data.filter(reg => reg && reg.exam);
          console.log('✅ examRegistrations updated:', this.examRegistrations.length, 'records');

          const filteredCount = data.length - this.examRegistrations.length;
          if (filteredCount > 0) {
            console.warn(`⚠️ Filtered out ${filteredCount} registrations with missing exam data`);
          }

          // Initialize receiptTypes Map with existing values from database
          this.examRegistrations.forEach(reg => {
            if (reg.typeReceipt) {
              this.receiptTypes.set(reg.id, reg.typeReceipt);
              console.log('📋 Loaded existing receipt type for registration:', reg.id, reg.typeReceipt);
            }
          });
        }

        this.calculateStatistics();

        console.log('📈 Statistics calculated:');
        console.log('  - Total Exams:', this.totalExams);
        console.log('  - Passed Exams:', this.passedExams);
        console.log('  - Failed Exams:', this.failedExams);

        this.cdr.detectChanges();
        console.log('🔄 Change detection triggered');
      },
      error: (error) => {
        console.error('❌ Unexpected error in subscribe:', error);
        this.error = 'เกิดข้อผิดพลาดที่ไม่คาดคิด: ' + error.message;
        this.cdr.detectChanges();
      },
      complete: () => {
        console.log('✅ Observable completed');
      }
    });
  }

  calculateStatistics(): void {
    this.totalExams = this.examRegistrations.length;
    this.passedExams = 0;
    this.failedExams = 0;
    this.pendingResultExams = 0;

    this.examRegistrations.forEach(registration => {
      // Safety check for exam
      if (!registration.exam) {
        console.warn('⚠️ Registration missing exam:', registration);
        return;
      }

      // นับเฉพาะรายการที่ประกาศผลแล้วเท่านั้น
      // รายการที่ยังไม่ประกาศผล (examResult = null) ต้องไม่ถูกนับเป็น "ไม่ผ่าน"
      const result = this.getExamResult(registration);

      if (result === true) {
        this.passedExams++;
      } else if (result === false) {
        this.failedExams++;
      } else {
        this.pendingResultExams++;
      }
    });
  }

  /**
   * ผลการสอบของรายการนี้
   * null/undefined = ยังไม่ประกาศผล, true = ผ่าน, false = ไม่ผ่าน
   * (รองรับข้อมูลเก่าที่ยังไม่มี examResult: ถ้า isCompleted = true ถือว่าผ่าน)
   */
  getExamResult(registration: any): boolean | null {
    if (registration?.examResult === true || registration?.examResult === false) {
      return registration.examResult;
    }

    return registration?.isCompleted === true ? true : null;
  }

  /**
   * แสดงผลการสอบให้ผู้สมัครเห็นเฉพาะกรณี "สอบผ่าน" เท่านั้น
   */
  isExamPassed(registration: any): boolean {
    return this.getExamResult(registration) === true;
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  formatPrice(price: string): string {
    const numPrice = parseFloat(price);
    return numPrice.toLocaleString('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  getValuerRankBadgeClass(valuerRank: string): string {
    const rankMap: { [key: string]: string } = {
      'วิสามัญ': 'badge-danger',
      'สามัญ': 'badge-warning',
      'วุฒิ': 'badge-success',
      'วุฒิ+smart pv': 'badge-info',
      'ระดับ 1': 'badge-primary',
      'ระดับ 2': 'badge-info',
      'ระดับ 3': 'badge-warning',
      'ระดับ 4': 'badge-danger'
    };
    return rankMap[valuerRank] || 'badge-secondary';
  }

  getExamStatusBadgeClass(statusId: number): string {
    switch (statusId) {
      case 1:
        return 'badge-secondary'; // Draft
      case 2:
        return 'badge-success'; // Open
      case 3:
        return 'badge-danger'; // Closed
      default:
        return 'badge-secondary';
    }
  }

  getExamStatusText(statusId: number): string {
    switch (statusId) {
      case 1:
        return 'ฉบับร่าง';
      case 2:
        return 'เปิดรับสมัคร';
      case 3:
        return 'ปิดรับสมัคร';
      default:
        return 'ไม่ทราบสถานะ';
    }
  }

  getRegistrationStatusBadgeClass(statusName: string): string {
    switch (statusName) {
      case 'ยืนยันแล้ว':
      case 'อนุมัติ':
      case 'Approved':
        return 'badge-success';
      case 'รอยืนยัน':
      case 'รอการอนุมัติ':
      case 'รอ Approve':
        return 'badge-warning';
      case 'ไม่อนุมัติ':
      case 'ยกเลิก':
      case 'ยกเลิกใบคำขอ':
        return 'badge-danger';
      case 'คำขอเก่า':
        return 'badge-secondary';
      default:
        return 'badge-secondary';
    }
  }

  getPaymentStatusBadgeClass(statusName: string): string {
    switch (statusName) {
      case 'ชำระเงินเสร็จสิ้น':
      case 'ชำระเงินแล้ว':
      case 'ชำระแล้ว':
        return 'badge-success';
      case 'รอชำระเงิน':
      case 'ยังไม่ได้ชำระเงิน':
        return 'badge-warning';
      case 'รอตรวจสอบยอดชำระ':
      case 'รอตรวจสอบ':
        return 'badge-info';
      case 'ยกเลิกการชำระ':
      case 'ยกเลิก':
        return 'badge-danger';
      default:
        return 'badge-secondary';
    }
  }

  isExamUpcoming(examDate: string): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const exam = new Date(examDate);
    exam.setHours(0, 0, 0, 0);
    return exam >= today;
  }

  isRegistrationOpen(registrationDeadline: string): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const deadline = new Date(registrationDeadline);
    deadline.setHours(0, 0, 0, 0);
    return deadline >= today;
  }

  goBack(): void {
    this.router.navigate(['/user/dashboard']);
  }

  retry(): void {
    console.log('Retrying to load exams...');
    this.loadUserExams();
  }

  /**
   * Get receipt type for a registration
   */
  getReceiptType(registrationId: string): string {
    return this.receiptTypes.get(registrationId) || '';
  }

  /**
   * Check if receipt type is already set from database (read-only)
   */
  isReceiptTypeReadOnly(registrationId: string): boolean {
    const registration = this.examRegistrations.find(reg => reg.id === registrationId);
    return !!(registration && registration.typeReceipt);
  }

  /**
   * Get receipt type label for display
   */
  getReceiptTypeLabel(type: string): string {
    if (type === 'individual') {
      return 'ออกใบเสร็จแบบบุคคลธรรมดา';
    } else if (type === 'company') {
      return 'ออกใบเสร็จแบบนิติบุคคล';
    }
    return '';
  }

  /**
   * Handle receipt type change and update database
   */
  onReceiptTypeChange(registrationId: string, type: string): void {
    // Prevent change if already set from database
    if (this.isReceiptTypeReadOnly(registrationId)) {
      console.log('Receipt type is read-only, cannot change:', registrationId);
      return;
    }

    this.receiptTypes.set(registrationId, type);
    console.log('Receipt type selected for registration:', registrationId, type);
    
    // Update database with typeReceipt field
    this.updateReceiptType(registrationId, type);
    
    this.cdr.detectChanges();
  }

  /**
   * Update receipt type in database
   */
  updateReceiptType(registrationId: string, type: string): void {
    const apiUrl = `${environment.apiUrl}/exam-lists/${registrationId}`;
    const payload = { typeReceipt: type };

    console.log('Updating receipt type in database:', registrationId, type);

    this.http.patch(apiUrl, payload).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error updating receipt type:', error);
        alert('เกิดข้อผิดพลาดในการบันทึกประเภทใบเสร็จ กรุณาลองใหม่อีกครั้ง');
        return of(null);
      })
    ).subscribe({
      next: (response) => {
        if (response) {
          console.log('Receipt type updated successfully:', response);
        }
      }
    });
  }

  /**
   * Navigate to the receipt/slip PDF page for an exam registration
   */
  downloadReceipt(registrationId: string): void {
    const receiptType = this.receiptTypes.get(registrationId);

    if (!receiptType) {
      alert('กรุณาเลือกประเภทใบเสร็จก่อนดาวน์โหลด');
      return;
    }

    console.log('📄 Loading receipt log for exam registration:', registrationId);
    console.log('📄 Receipt type:', receiptType);

    // หน้าใบเสร็จของการสมัครสอบใช้ "รหัส receipt log" ไม่ใช่รหัสใบสมัคร
    // จึงต้องดึง receipt log ของใบสมัครนี้มาก่อน
    const apiUrl = `${environment.apiUrl}/receipt-log/exam-list/${registrationId}`;

    this.http.get<{ data: any[] }>(apiUrl).subscribe({
      next: (response) => {
        const logs = response?.data || [];
        const activeLog = logs.find(log => log?.type === 'created') || null;

        if (!activeLog) {
          alert('ยังไม่ได้ออกใบเสร็จสำหรับรายการนี้ กรุณาติดต่อเจ้าหน้าที่');
          return;
        }

        const receiptUrl = `/user/renew-slip/${activeLog.id}?type=exam&receiptType=${receiptType}`;
        this.router.navigateByUrl(receiptUrl);
      },
      error: (error: HttpErrorResponse) => {
        console.error('❌ Error loading receipt log:', error);
        alert(`ไม่สามารถเปิดใบเสร็จได้: ${error.message}`);
      }
    });
  }

  viewExamDetails(examId: string): void {
    console.log('Viewing exam details for:', examId);
    // TODO: Navigate to exam detail page or open modal
    // this.router.navigate(['/user/exam-detail', examId]);
  }

  onPaymentSlipSelected(event: Event, registrationId: string): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        alert('กรุณาอัปโหลดไฟล์ JPG, JPEG หรือ PNG เท่านั้น');
        input.value = '';
        return;
      }

      // Validate file size (5MB max)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        alert('ขนาดไฟล์ต้องไม่เกิน 5MB');
        input.value = '';
        return;
      }

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        const preview = e.target?.result as string;

        // Store the file with preview
        this.selectedPaymentSlips.set(registrationId, {
          file: file,
          fileName: file.name,
          preview: preview
        });

        this.cdr.detectChanges();
        console.log('Payment slip selected for registration:', registrationId, file.name);
      };

      reader.readAsDataURL(file);
    }
  }

  getSelectedFileName(registrationId: string): string | null {
    const slipData = this.selectedPaymentSlips.get(registrationId);
    return slipData ? slipData.fileName : null;
  }

  getPaymentSlipPreview(registrationId: string): string | null {
    const slipData = this.selectedPaymentSlips.get(registrationId);
    return slipData ? slipData.preview : null;
  }

  removePaymentSlipPreview(registrationId: string): void {
    this.selectedPaymentSlips.delete(registrationId);

    // Reset the file input
    const fileInput = document.getElementById('paymentSlip-' + registrationId) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }

    this.cdr.detectChanges();
  }

  openImageModal(imageUrl: string): void {
    this.imageModalUrl = imageUrl;
    this.cdr.detectChanges();
  }

  closeImageModal(): void {
    this.imageModalUrl = null;
    this.cdr.detectChanges();
  }

  isUploadingSlip(registrationId: string): boolean {
    return this.uploadingSlips.has(registrationId);
  }

  /**
   * Handle transfer date-time change
   */
  onTransferDateTimeChange(registrationId: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    const dateTimeValue = input.value;

    if (dateTimeValue) {
      // Convert datetime-local format (YYYY-MM-DDTHH:mm) to Thai readable format
      const dateTime = new Date(dateTimeValue);
      const formattedDateTime = this.formatTransferDateTime(dateTime);
      
      this.transferDateTimes.set(registrationId, formattedDateTime);
      console.log('Transfer date-time selected for registration:', registrationId, formattedDateTime);
      this.cdr.detectChanges();
    }
  }

  /**
   * Format date-time to Thai readable text format
   */
  formatTransferDateTime(date: Date): string {
    const thaiMonths = [
      'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
      'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
    ];

    const day = date.getDate();
    const month = thaiMonths[date.getMonth()];
    const year = date.getFullYear() + 543; // Convert to Buddhist year
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day} ${month} ${year} เวลา ${hours}:${minutes} น.`;
  }

  /**
   * Get transfer date-time for a registration
   */
  getTransferDateTime(registrationId: string): string {
    return this.transferDateTimes.get(registrationId) || '';
  }

  submitPaymentSlip(registrationId: string): void {
    const slipData = this.selectedPaymentSlips.get(registrationId);

    if (!slipData) {
      alert('กรุณาเลือกไฟล์สลิปการชำระเงินก่อน');
      return;
    }

    // Validate transfer date-time is selected
    const transferDateTime = this.transferDateTimes.get(registrationId);
    if (!transferDateTime) {
      alert('กรุณาระบุวันที่และเวลาการโอนเงินก่อน');
      return;
    }

    // Confirm before upload
    if (!confirm('คุณต้องการส่งสลิปการชำระเงินใช่หรือไม่?')) {
      return;
    }

    this.uploadPaymentSlip(registrationId, slipData.file, transferDateTime);
  }

  uploadPaymentSlip(registrationId: string, file: File, transferDateTime: string): void {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('transferDateTime', transferDateTime);

    const apiUrl = `${environment.apiUrl}/exam-lists/${registrationId}/upload-payment-slip`;

    console.log('Uploading payment slip to:', apiUrl);
    console.log('Transfer DateTime:', transferDateTime);
    this.uploadingSlips.add(registrationId);
    this.cdr.detectChanges();

    this.http.post(apiUrl, formData).pipe(
      timeout(30000),
      catchError((error: HttpErrorResponse) => {
        console.error('Error uploading payment slip:', error);

        let errorMessage = 'เกิดข้อผิดพลาดในการอัปโหลดสลิป';

        if (error.status === 0) {
          errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้';
        } else if (error.status === 400) {
          errorMessage = 'ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบไฟล์';
        } else if (error.status === 404) {
          errorMessage = 'ไม่พบรายการสอบนี้';
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        }

        alert(errorMessage + ' กรุณาลองใหม่อีกครั้ง');
        this.uploadingSlips.delete(registrationId);
        this.cdr.detectChanges();

        return of(null);
      }),
      finalize(() => {
        this.uploadingSlips.delete(registrationId);
        this.cdr.detectChanges();
      })
    ).subscribe({
      next: (response) => {
        if (response) {
          console.log('Payment slip uploaded successfully:', response);

          // Patch examListPaymentStatusId = 2 (รอตรวจสอบ) and transferDateTime
          this.patchPaymentStatus(registrationId, 2, transferDateTime);
        }
      }
    });
  }

  patchPaymentStatus(registrationId: string, statusId: number, transferDateTime: string): void {
    const patchUrl = `${environment.apiUrl}/exam-lists/${registrationId}`;
    const patchData = { 
      examListPaymentStatusId: statusId,
      transferDateTime: transferDateTime
    };

    console.log('Patching exam-lists with data:', patchData);

    this.http.patch(patchUrl, patchData).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error patching payment status and transfer datetime:', error);
        // Non-blocking: still show success and reload
        return of(null);
      })
    ).subscribe(() => {
      alert('ส่งสลิปการชำระเงินสำเร็จ! รอเจ้าหน้าที่ตรวจสอบ');

      // Clear the selected file and transfer datetime
      this.selectedPaymentSlips.delete(registrationId);
      this.transferDateTimes.delete(registrationId);

      // Reload to get updated status
      this.loadUserExams();
    });
  }
}
